import { StickyNote as StickyNoteIcon, PanelLeft, Search, X, Maximize2, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, BookOpen, BookText, Highlighter, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';

export default function PDFViewerHeader({
  notePlacementMode,
  showStickyHeader,
  isSidebarOpen,
  toggleSidebar,
  isSearchOpen,
  toggleSearch,
  searchQuery,
  setSearchQuery,
  isSearching,
  searchResults,
  currentSearchIndex,
  handlePrevResult,
  handleNextResult,
  sections,
  currentSectionIndex,
  goToPreviousSection,
  goToNextSection,
  hasLearningPack,
  onLearningPackClick,
  learningPackCountForCurrentSection,
  learningPackTitle,
  sectionTitle,
  onAddNote,
  onViewNotes,
  onViewHighlights,
  pageInputValue,
  handlePageInputChange,
  handlePageInputSubmit,
  numPages,
  toggleFullscreen,
  isFullscreen,
}) {
  return (
    <>
      {notePlacementMode && (
        <div className="sticky top-0 z-50 bg-yellow-500 border-b-2 border-yellow-600 shadow-lg" style={{ flexShrink: 0 }}>
          <div className="px-4 py-3 flex items-center justify-center">
            <div className="flex items-center gap-3">
              <StickyNoteIcon className="w-5 h-5 text-yellow-900 animate-pulse" />
              <p className="text-yellow-900 font-bold text-lg">Click anywhere on the PDF to add a note</p>
              <StickyNoteIcon className="w-5 h-5 text-yellow-900 animate-pulse" />
            </div>
          </div>
        </div>
      )}

      {showStickyHeader && (
        <div
          className={`sticky ${notePlacementMode ? 'top-[60px]' : 'top-0'} z-40 bg-[hsl(var(--card-bg))] border-b border-[hsl(var(--card-border))] px-3 py-2 shadow-sm`}
          style={{ flexShrink: 0 }}
        >
          <div className="flex items-center justify-between gap-3 w-full">
            <div className="flex items-center gap-1 flex-shrink-0">
              <Button
                onClick={toggleSidebar}
                variant={isSidebarOpen ? 'secondary' : 'ghost'}
                size="icon"
                className={isSidebarOpen ? 'bg-[hsl(var(--sidebar-hover))]' : ''}
                title="Toggle Sidebar"
              >
                <PanelLeft className="w-4 h-4 text-[hsl(var(--primary))]" />
              </Button>

              <div className="relative">
                <Button
                  onClick={toggleSearch}
                  variant={isSearchOpen ? 'secondary' : 'ghost'}
                  size="icon"
                  className={isSearchOpen ? 'bg-[hsl(var(--sidebar-hover))]' : ''}
                  title="Search"
                >
                  <Search className="w-4 h-4 text-[hsl(var(--primary))]" />
                </Button>
                {isSearchOpen && (
                  <div className="absolute top-full left-0 mt-2 z-50 bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] p-2 rounded-md shadow-lg flex flex-col gap-2 animate-in fade-in slide-in-from-top-1 w-72">
                    <div className="flex gap-2">
                      <Input
                        placeholder="Find..."
                        className="h-8 text-xs bg-[hsl(var(--main-bg))]"
                        autoFocus
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                      <Button size="sm" variant="ghost" className="h-8 px-2" onClick={toggleSearch}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>

                    <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
                      <div className="flex items-center gap-1">
                        {isSearching ? (
                          <span className="flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Searching...</span>
                        ) : (
                          <span>
                            {searchResults.length > 0
                              ? `${currentSearchIndex + 1} of ${searchResults.length} matches`
                              : searchQuery ? 'No results' : 'Type to search'}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-6 w-6"
                          onClick={handlePrevResult}
                          disabled={searchResults.length === 0}
                          title="Previous match"
                        >
                          <ChevronUp className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-6 w-6"
                          onClick={handleNextResult}
                          disabled={searchResults.length === 0}
                          title="Next match"
                        >
                          <ChevronDown className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex-1 flex items-center justify-center gap-2 min-w-0 px-4">
              {sections.length > 0 && (
                <Button
                  onClick={goToPreviousSection}
                  disabled={currentSectionIndex === 0}
                  variant="ghost"
                  size="icon"
                  className="hover:bg-[hsl(var(--sidebar-hover))] disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0"
                  aria-label="Previous section"
                >
                  <ChevronLeft className="w-5 h-5 text-[hsl(var(--primary))]" />
                </Button>
              )}

              <div className="min-w-0 flex-1 flex justify-center w-full">
                {hasLearningPack ? (
                  <Button
                    onClick={onLearningPackClick}
                    variant="outline"
                    size="sm"
                    className="w-full justify-center bg-[hsl(var(--card-bg))] border border-[hsl(var(--primary))] text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))] hover:text-white font-semibold shadow-sm px-4"
                    title={`View Learning Pack (${learningPackCountForCurrentSection} activities in this section)`}
                  >
                    <BookOpen className="w-4 h-4 mr-2 flex-shrink-0" />
                    <span className="min-w-0 truncate">{learningPackTitle}</span>
                    <span className="ml-2 flex-shrink-0 opacity-80">({learningPackCountForCurrentSection})</span>
                  </Button>
                ) : (
                  !!sectionTitle && (
                    <div className="text-sm font-semibold text-white truncate text-center">{sectionTitle}</div>
                  )
                )}
              </div>

              {sections.length > 0 && (
                <Button
                  onClick={goToNextSection}
                  disabled={currentSectionIndex === sections.length - 1}
                  variant="ghost"
                  size="icon"
                  className="hover:bg-[hsl(var(--sidebar-hover))] disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0"
                  aria-label="Next section"
                >
                  <ChevronRight className="w-5 h-5 text-[hsl(var(--primary))]" />
                </Button>
              )}
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              {(onAddNote || onViewNotes) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-yellow-600 hover:bg-yellow-100/10 hover:text-yellow-500"
                      title="Notes"
                    >
                      <StickyNoteIcon className="w-5 h-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-[hsl(var(--card-bg))] text-white border-[hsl(var(--card-border))]">
                    {onAddNote && (
                      <DropdownMenuItem
                        onClick={onAddNote}
                        className="text-gray-100 cursor-pointer hover:bg-[hsl(var(--primary))]/20 hover:text-white data-[highlighted]:bg-[hsl(var(--primary))]/20 data-[highlighted]:text-white"
                      >
                        <StickyNoteIcon className="w-4 h-4 mr-2" />
                        Add Note
                      </DropdownMenuItem>
                    )}
                    {onViewNotes && (
                      <DropdownMenuItem
                        onClick={onViewNotes}
                        className="text-gray-100 cursor-pointer hover:bg-[hsl(var(--primary))]/20 hover:text-white data-[highlighted]:bg-[hsl(var(--primary))]/20 data-[highlighted]:text-white"
                      >
                        <BookText className="w-4 h-4 mr-2" />
                        View Notes
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {onViewHighlights && (
                <Button
                  onClick={onViewHighlights}
                  variant="ghost"
                  size="icon"
                  className="text-gray-400 hover:bg-gray-100/10 hover:text-gray-200"
                  title="Highlights"
                >
                  <Highlighter className="w-5 h-5" />
                </Button>
              )}

              <div className="flex items-center gap-1 mx-2">
                <Input
                  value={pageInputValue}
                  onChange={handlePageInputChange}
                  onKeyDown={handlePageInputSubmit}
                  className="w-12 h-7 px-1 text-center text-xs font-medium bg-muted/30 focus:bg-background transition-colors"
                />
                <span className="text-xs text-muted-foreground font-medium whitespace-nowrap">/ {numPages || '-'}</span>
              </div>

              <div className="h-4 w-px bg-border mx-1" />

              <Button
                onClick={toggleFullscreen}
                variant="ghost"
                size="icon"
                className="hover:bg-[hsl(var(--sidebar-hover))]"
                aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              >
                <Maximize2 className="w-4 h-4 text-[hsl(var(--primary))]" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
